import { PERFORMANCE_BADGE_LABELS } from '@tempmee/core';
import type { CandidateCardAttendance, CandidateDetailsBadge } from '@tempmee/react-blocks';
import { BadgeTypes } from '~/constants';
import { BestInDentalStatus } from '~/enums/BestInDental';
import { IUser } from '~/models';

/** A candidate's badge as `/jobs/:jobId/candidates` returns it. */
type TCandidateBadge = IUser['badges'][number];

/**
 * Attendance badge level → the design system's attendance tier.
 *
 * The backend grades attendance 1–3 (bronze/silver/gold) with 0 reserved for a
 * newly verified professional. The DS narrows `CandidateCard`'s badge to the
 * three earned tiers, so level 0 has nowhere to land on a card.
 */
export const ATTENDANCE_BY_LEVEL: Record<number, CandidateCardAttendance> = {
  1: 'great-attendance',
  2: 'elite-attendance',
  3: 'perfect-attendance',
};

/** Badge type → the DS badge artwork for the achievement strip. */
const BADGE_VARIANT_BY_TYPE = {
  [BadgeTypes.Timeliness]: 'timeliness',
  [BadgeTypes.Knowledge]: 'knowledge',
  [BadgeTypes.Teamwork]: 'teamwork',
} as const;

const findBadge = (
  candidate: IUser | null | undefined,
  type: string,
): TCandidateBadge | undefined => candidate?.badges?.find((badge) => badge?.type === type);

/** Whether the candidate is a current Best in Dental winner. */
export const isBestInDentalWinner = (candidate: IUser | null | undefined): boolean =>
  candidate?.professional_info?.bestInDental?.status === BestInDentalStatus.Winner;

/** Completed shifts, as both the card and the profile panel headline them. */
export const toShiftCount = (candidate: IUser | null | undefined): number =>
  candidate?.completedJobs ?? candidate?.completedShiftCount ?? 0;

/**
 * The candidate's attendance tier, or `undefined` when the backend has not
 * graded them — level 0, which is a real state and not the lowest tier.
 *
 * `CandidateCard` and `CandidateTopper` both take the tier as optional and draw
 * no badge without one, so this maps straight onto their contract.
 */
export const toAttendance = (
  candidate: IUser | null | undefined,
): CandidateCardAttendance | undefined =>
  ATTENDANCE_BY_LEVEL[findBadge(candidate, BadgeTypes.AttendanceScore)?.level ?? 0];

/**
 * Candidate → the achievement strip in `CandidateDetails`.
 *
 * Ordered to match the design: Best in Dental leads when the professional holds
 * it (it is the rarest of the set), then attendance, completed shifts, and the
 * multi-earn badges.
 *
 * Every badge carries its caption. The design system uses `label` only as the
 * accessible name when it is omitted, so leaving it off renders the artwork with
 * no text under it — and the Figma captions all five.
 */
export const toCandidateBadges = (candidate: IUser | null | undefined): CandidateDetailsBadge[] => {
  const badges: CandidateDetailsBadge[] = [];

  const withLabel = (badge: CandidateDetailsBadge): CandidateDetailsBadge => ({
    label: PERFORMANCE_BADGE_LABELS[badge.variant],
    ...badge,
  });

  if (isBestInDentalWinner(candidate)) {
    badges.push(withLabel({ variant: 'best-in-dental' }));
  }

  const attendanceBadge = findBadge(candidate, BadgeTypes.AttendanceScore);
  const attendanceVariant = ATTENDANCE_BY_LEVEL[attendanceBadge?.level ?? 0];

  if (attendanceVariant) {
    badges.push(withLabel({ variant: attendanceVariant }));
  } else if (attendanceBadge) {
    // Level 0 is a newly verified professional — the one attendance state the
    // achievement strip *can* represent honestly, unlike `CandidateCard`.
    badges.push(withLabel({ variant: 'rising-star' }));
  }

  const shiftCount = toShiftCount(candidate);

  if (findBadge(candidate, BadgeTypes.CompletedShifts) && shiftCount > 0) {
    badges.push(withLabel({ variant: 'shift-count', count: shiftCount }));
  }

  Object.entries(BADGE_VARIANT_BY_TYPE).forEach(([type, variant]) => {
    const badge = findBadge(candidate, type);

    if (badge) {
      badges.push(withLabel({ variant, count: badge.badgeCount }));
    }
  });

  return badges;
};
